import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { dhaftharuApi } from '../features/dhaftharu/api.ts';
import { dv } from '../i18n/dv.ts';

type RegisterKind = 'MANUAL' | 'CONDITIONAL';

export function DhaftharuList(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['dhaftharu'], queryFn: () => dhaftharuApi.list() });

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<RegisterKind>('MANUAL');
  const [error, setError] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      dhaftharuApi.create({
        title: title.trim(),
        description: description.trim() || undefined,
        ...(kind === 'CONDITIONAL' ? { conditions: [] } : {}),
      }),
    onSuccess: (d) => {
      setShowForm(false); setTitle(''); setDescription(''); setKind('MANUAL'); setError(false);
      void qc.invalidateQueries({ queryKey: ['dhaftharu'] });
      void navigate({ to: '/dhaftharu/$id', params: { id: d.id } });
    },
    onError: () => setError(true),
  });

  const remove = useMutation({
    mutationFn: (id: string) => dhaftharuApi.remove(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dhaftharu'] }); },
  });

  function onDelete(id: string): void {
    if (window.confirm(dv.dhaftharu.deleteConfirm)) remove.mutate(id);
  }

  const list = data ?? [];

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl tracking-tight">{dv.dhaftharu.title}</h1>
        <button onClick={() => { setShowForm((v) => !v); setError(false); setKind('MANUAL'); }} className="btn-primary">
          <Icon name="add" size={15} /> {dv.dhaftharu.create}
        </button>
      </header>

      {showForm && (
        <section className="card-paper space-y-3 p-4">
          <h2 className="status-label text-muted-foreground">{dv.dhaftharu.create}</h2>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); setError(false); create.mutate(); }}
          >
            <div className="space-y-1">
              <label className="field-label">{dv.dhaftharu.titleField}</label>
              <input required className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="field-label">{dv.dhaftharu.description}</label>
              <textarea className="input min-h-[4rem]" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-1">
              <span className="field-label">{dv.dhaftharu.kindLabel}</span>
              <div className="flex gap-2" role="group" aria-label={dv.dhaftharu.kindLabel}>
                <button
                  type="button"
                  onClick={() => setKind('MANUAL')}
                  className={kind === 'MANUAL' ? 'btn-primary' : 'btn-ghost'}
                >
                  {dv.dhaftharu.kindManual}
                </button>
                <button
                  type="button"
                  onClick={() => setKind('CONDITIONAL')}
                  className={kind === 'CONDITIONAL' ? 'btn-primary' : 'btn-ghost'}
                >
                  <Icon name="auto_awesome" size={13} />
                  {dv.dhaftharu.kindConditional}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">{dv.dhaftharu.kindHelp}</p>
            </div>
            {error && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={create.isPending} className="btn-primary">
                {create.isPending ? dv.common.saving : dv.common.create}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">{dv.common.cancel}</button>
            </div>
          </form>
        </section>
      )}

      {isLoading ? (
        <p className="status-label text-muted-foreground" role="status">{dv.common.loading}</p>
      ) : list.length === 0 ? (
        <p className="card-paper p-6 text-center text-sm text-muted-foreground">{dv.dhaftharu.empty}</p>
      ) : (
        <ul className="space-y-2">
          {list.map((d) => (
            <li key={d.id} className="card-paper flex items-center justify-between gap-3 p-4">
              <Link to="/dhaftharu/$id" params={{ id: d.id }} className="flex min-w-0 flex-1 items-center gap-3">
                <Icon name="menu_book" size={20} className="shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="font-medium leading-snug truncate">{d.title}</p>
                  {d.conditions !== null ? (
                    <span className="pill pill-active inline-flex items-center gap-1">
                      <Icon name="auto_awesome" size={10} />
                      {dv.dhaftharu.smart}
                    </span>
                  ) : (
                    <p className="mono-meta text-muted-foreground">{d._count.entries} {dv.dhaftharu.entries}</p>
                  )}
                </div>
              </Link>
              <button
                onClick={() => onDelete(d.id)}
                disabled={remove.isPending}
                aria-label={dv.dhaftharu.delete}
                className="btn-ghost px-2 py-1 text-destructive hover:text-destructive"
              >
                <Icon name="delete" size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
